import { useMemo } from 'react';

import { usePublicDatasetSessionsBulk } from '@/hooks/useSwr';
import { SessionSchema } from '@/types/schema';

import { TPublicDataset } from '../types';

export const useAgentDatasetMetrics = ({
  datasets,
  agentId,
}: {
  datasets: TPublicDataset[];
  agentId: string;
}) => {
  const { data: tracerSessionsBulk } = usePublicDatasetSessionsBulk(
    datasets?.length
      ? {
          share_tokens: datasets.map((dataset) => dataset.share_token),
        }
      : null
  );

  const relevantSessions = useMemo(
    () =>
      tracerSessionsBulk?.filter(
        (s) => s.extra?.metadata?.project_id === agentId
      ),
    [tracerSessionsBulk, agentId]
  );

  const p50Latency = useMemo(
    () =>
      relevantSessions?.length
        ? relevantSessions.reduce(
            (acc, cur) => acc + (cur.latency_p50 ?? 0),
            0
          ) / relevantSessions.length
        : undefined,
    [relevantSessions]
  );

  const sortedRelevantSessions = useMemo(() => {
    return relevantSessions?.sort(
      (a, b) =>
        new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
    );
  }, [relevantSessions]);

  const datasetToMostRecentSession = useMemo(() => {
    return datasets.reduce((acc, cur) => {
      acc[cur.id] = sortedRelevantSessions?.filter(
        (s) => s.reference_dataset_id === cur.id
      )?.[0];
      return acc;
    }, {} as Record<string, SessionSchema | undefined>);
  }, [datasets, sortedRelevantSessions]);

  return {
    p50Latency,
    datasetToMostRecentSession,
  };
};
